import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BRAND } from "../lib/brand";
import SyncBadge from "./SyncBadge";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Users, FileText, LogOut, Plus, UsersRound, CalendarClock, KeyRound, Upload } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", Icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", Icon: Users, testid: "nav-customers" },
  { to: "/reports", label: "Reports", Icon: FileText, testid: "nav-reports" },
];

const ADMIN_NAV = [
  { to: "/team", label: "Team", Icon: UsersRound, testid: "nav-team" },
  { to: "/schedule", label: "Schedule", Icon: CalendarClock, testid: "nav-schedule" },
  { to: "/import", label: "Import", Icon: Upload, testid: "nav-import" },
];

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const navItems = isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;
  const [pwOpen, setPwOpen] = useState(false);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  // The report wizard renders its own fixed bottom action bar, so the global
  // bottom nav must be hidden there to avoid covering the Next/Back buttons.
  const hideBottomNav =
    location.pathname.startsWith("/report/new") ||
    location.pathname.endsWith("/edit");

  return (
    <div className={`min-h-screen bg-background ${hideBottomNav ? "" : "pb-20 md:pb-0"}`}>
      <header className="sticky top-0 z-30 border-b border-border bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-2.5">
          <button
            onClick={() => navigate("/")}
            data-testid="brand-home"
            className="flex items-center gap-2.5"
          >
            <img src={BRAND.logoMark} alt={BRAND.name} className="h-9 w-9 rounded-full" />
            <div className="hidden sm:block text-left leading-tight">
              <div className="font-heading text-sm font-extrabold text-slate-900">
                {BRAND.name}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-primary">
                Field Service
              </div>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, label, Icon, testid }) => (
              <button
                key={to}
                data-testid={`${testid}-desktop`}
                onClick={() => navigate(to)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  isActive(to)
                    ? "bg-primary/10 text-primary"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <SyncBadge />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-xs font-bold text-white"
                >
                  {(user?.name || "?")
                    .split(" ")
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-xs capitalize text-slate-500">{user?.role}</p>
                </div>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/team")} data-testid="menu-team" className="md:hidden">
                      <UsersRound className="mr-2 h-4 w-4" /> Team
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/schedule")} data-testid="menu-schedule" className="md:hidden">
                      <CalendarClock className="mr-2 h-4 w-4" /> Schedule
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/import")} data-testid="menu-import" className="md:hidden">
                      <Upload className="mr-2 h-4 w-4" /> Import
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="md:hidden" />
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu">
                  <KeyRound className="mr-2 h-4 w-4" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-5">{children}</main>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {/* mobile bottom nav */}
      {!hideBottomNav && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-white md:hidden">
          <div className="mx-auto grid max-w-5xl grid-cols-4">
            {NAV.map(({ to, label, Icon, testid }) => (
              <button
                key={to}
                data-testid={testid}
                onClick={() => navigate(to)}
                className={`flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold ${
                  isActive(to) ? "text-primary" : "text-slate-500"
                }`}
              >
                <Icon className="h-5 w-5" /> {label}
              </button>
            ))}
            <button
              data-testid="nav-new-report"
              onClick={() => navigate("/report/choose")}
              className="flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold text-primary"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
                <Plus className="h-4 w-4" />
              </span>
              New
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
